import { DEFAULT_RULES, PROTOCOL, type RoomRules, type RoomSummary } from '@uno/shared';
import { useEffect, useState } from 'react';
import { useAppState, useT } from '../hooks';
import { fmt, plural } from '../i18n';
import { net } from '../net';
import { RulesEditor } from './RulesEditor';

interface Props {
  rooms: RoomSummary[];
  joinError: string | null;
  /** Server told us this room needs a password (e.g. after a deep-link join). */
  passwordPromptFor: string | null;
}

export function Lobby({ rooms, joinError, passwordPromptFor }: Props) {
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [passwordFor, setPasswordFor] = useState<string | null>(null);
  const [password, setPassword] = useState('');

  useEffect(() => {
    net.listRooms();
  }, []);

  const attemptJoin = (roomId: string, locked: boolean) => {
    if (locked) {
      setPasswordFor(roomId);
      setPassword('');
      return;
    }
    net.joinRoom(roomId);
  };

  // Either the user clicked a locked room, or the server bounced a join.
  const promptFor = passwordFor ?? passwordPromptFor;
  const closePrompt = () => {
    setPasswordFor(null);
    net.clearPasswordPrompt();
  };

  return (
    <div className="lobby">
      <section className="lobby__hero">
        <h1 className="lobby__title">{t.lobby.heroTitle}</h1>
        <p className="lobby__sub">{t.lobby.heroSubtitle}</p>
        <div className="lobby__cta">
          <button type="button" className="btn btn--primary btn--lg" onClick={() => setShowCreate(true)}>
            {t.lobby.createRoom}
          </button>
          <form
            className="joinbar"
            onSubmit={(e) => {
              e.preventDefault();
              const code = joinCode.trim().toUpperCase();
              if (code) net.joinRoom(code, password || undefined);
            }}
          >
            <input
              className="input"
              value={joinCode}
              placeholder={t.lobby.roomCode}
              maxLength={8}
              autoCapitalize="characters"
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              aria-label={t.lobby.roomCode}
            />
            <button type="submit" className="btn" disabled={!joinCode.trim()}>
              {t.common.join}
            </button>
          </form>
        </div>
        {joinError && <p className="form-error" role="alert">{joinError}</p>}
      </section>

      <section className="lobby__rooms">
        <div className="section-head">
          <h2>{t.lobby.publicRooms}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => net.listRooms()}>
            {t.common.refresh}
          </button>
        </div>

        {rooms.length === 0 ? (
          <p className="empty">{t.lobby.empty}</p>
        ) : (
          <ul className="roomlist">
            {rooms.map((room) => {
              const full = room.playerCount + room.botCount >= room.maxPlayers;
              const running = room.phase !== 'LobbyWaiting' && room.phase !== 'GameOver';
              const blocked = full || running;
              return (
                <li key={room.id}>
                  <button
                    type="button"
                    className="roomcard"
                    disabled={blocked}
                    onClick={() => attemptJoin(room.id, room.hasPassword)}
                  >
                    <span className="roomcard__main">
                      <span className="roomcard__name">
                        {room.hasPassword && <span title={t.lobby.passwordLabel}>🔒 </span>}
                        {room.name}
                      </span>
                      <span className="roomcard__meta">
                        {room.id} · {fmt(t.lobby.cardMeta, { host: room.hostNickname, seconds: room.rules.turnSeconds })}
                        {room.rules.stackDrawTwo || room.rules.stackDrawFour ? ` · ${t.lobby.stacking}` : ''}
                      </span>
                    </span>
                    <span className="roomcard__side">
                      <span className="roomcard__count">
                        {room.playerCount + room.botCount}/{room.maxPlayers}
                        {room.botCount > 0 && <em className="roomcard__bots"> · {room.botCount}🤖</em>}
                      </span>
                      <span className={`badge badge--${running ? 'live' : full ? 'full' : 'open'}`}>
                        {running ? t.lobby.badgeLive : full ? t.lobby.badgeFull : t.lobby.badgeOpen}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showCreate && <CreateRoomModal onClose={() => setShowCreate(false)} />}

      {promptFor && (
        <div className="modal-backdrop" onClick={closePrompt}>
          <form
            className="modal modal--sm"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              net.joinRoom(promptFor, password);
              setPasswordFor(null);
            }}
          >
            <h2 className="modal__title">{fmt(t.lobby.lockedTitle, { id: promptFor })}</h2>
            <input
              className="input input--lg"
              type="password"
              autoFocus
              value={password}
              placeholder={t.lobby.password}
              onChange={(e) => setPassword(e.target.value)}
              aria-label={t.lobby.passwordLabel}
            />
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={closePrompt}>
                {t.common.cancel}
              </button>
              <button type="submit" className="btn btn--primary">
                {t.common.join}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CreateRoomModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { nickname } = useAppState();
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [password, setPassword] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [rules, setRules] = useState<RoomRules>({ ...DEFAULT_RULES });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          net.createRoom({
            // Fall back to a localized default rather than letting the server
            // invent an English one.
            name: name.trim() || fmt(t.room.defaultRoomName, { name: nickname }),
            isPublic,
            password: password.trim() || undefined,
            maxPlayers,
            rules,
          });
          onClose();
        }}
      >
        <h2 className="modal__title">{t.create.title}</h2>

        <label className="field">
          <span className="field__label">{t.create.roomName}</span>
          <input
            className="input"
            value={name}
            maxLength={PROTOCOL.ROOM_NAME_MAX}
            placeholder={t.create.roomNamePlaceholder}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">{t.create.maxPlayers}</span>
            <select className="input" value={maxPlayers} onChange={(e) => setMaxPlayers(Number(e.target.value))}>
              {Array.from({ length: PROTOCOL.MAX_PLAYERS - PROTOCOL.MIN_PLAYERS + 1 }, (_, i) => i + PROTOCOL.MIN_PLAYERS).map(
                (n) => (
                  <option key={n} value={n}>
                    {plural(t.create.playersOption, n)}
                  </option>
                ),
              )}
            </select>
          </label>

          <label className="field">
            <span className="field__label">{t.create.passwordOptional}</span>
            <input
              className="input"
              type="password"
              value={password}
              placeholder={t.create.passwordPlaceholder}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        </div>

        <label className="toggle">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
          <span>{t.create.listPublic}</span>
        </label>

        <details className="details" open>
          <summary>{t.create.houseRules}</summary>
          <RulesEditor rules={rules} onChange={(patch) => setRules((r) => ({ ...r, ...patch }))} />
        </details>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn--primary">
            {t.create.submit}
          </button>
        </div>
      </form>
    </div>
  );
}
